/// <amd-module name="Repos/Views/Settings/ProjectBranchPolicies/Components/ProjectBranchPoliciesView" />
import * as React from "react";
import * as Resources from "Repos/Views/Settings/ProjectBranchPolicies/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Observer } from "VSSUI/Observer";
import { ZeroData } from "VSSUI/ZeroData";
import { IProjectBranchPoliciesService } from "../Contracts";
import { AddProjectBranchPoliciesPanel } from "./AddProjectBranchPoliciesPanel";
import { ProjectBranchPoliciesList } from "./ProjectBranchPoliciesList";

export interface IProjectBranchPoliciesViewProps extends IVssComponentProperties {
    setSelectedRefs: (value: string) => void;
}

export class ProjectBranchPoliciesView extends VssComponent<IProjectBranchPoliciesViewProps, {}> {
    private readonly zeroDataImagePath: string;

    private readonly projectBranchPoliciesService: IProjectBranchPoliciesService;

    private readonly showAddPolicyPanel = new ObservableValue<boolean>(false);

    public constructor(props: IProjectBranchPoliciesViewProps, context: IVssComponentContext) {
        super(props, context);

        this.zeroDataImagePath = getAssetLocation(
            context.pageContext,
            "ms.vss-code-web/settings-project-branch-policies-content/Content/ZeroData.svg"
        );

        this.projectBranchPoliciesService = context.pageContext.getService<IProjectBranchPoliciesService>("IProjectBranchPoliciesService");

        // need to reload every time we recreate this component instead of on dismiss of create panel because a new policy that would
        // show up here isnt acutally create until a user has made changes on the cross-repo policy page
        this.projectBranchPoliciesService.reloadProjectBranchPoliciesAsync();
    }

    public render(): JSX.Element {
        const { showAddPolicyPanel, projectBranchPoliciesService } = this;

        const { projectBranchPoliciesLoading, projectBranchPolicies, hasEditPermission } = projectBranchPoliciesService;

        return (
            <>
                <Observer {...{ projectBranchPoliciesLoading, projectBranchPolicies, hasEditPermission }}>
                    {() => {
                        const showZeroDataExperience = !projectBranchPoliciesLoading.value && projectBranchPolicies.length === 0;

                        return (
                            <Card
                                className="flex-grow padding-bottom-0 policies"
                                titleProps={{ text: Resources.ProjectBranchPoliciesCardTitle }}
                                headerDescriptionProps={{ text: Resources.ProjectBranchPoliciesCardDescription }}
                                contentProps={{ contentPadding: false }}
                                headerCommandBarItems={[
                                    {
                                        ariaLabel: Resources.AddProjectPolicyAria,
                                        iconProps: { iconName: "Add" },
                                        id: "addProjectPolicy",
                                        disabled: !hasEditPermission.value,
                                        onActivate: this.addPolicyOnClick
                                    }
                                ]}
                            >
                                <div className="flex-column flex-grow rhythm-vertical-16">
                                    {showZeroDataExperience ? (
                                        this.zeroDataCard(hasEditPermission.value)
                                    ) : (
                                        <ProjectBranchPoliciesList
                                            key="list"
                                            projectBranchPolicies={projectBranchPolicies.value}
                                            setSelectedRefs={this.props.setSelectedRefs}
                                        />
                                    )}
                                </div>
                            </Card>
                        );
                    }}
                </Observer>

                <ConditionalChildren renderChildren={showAddPolicyPanel}>
                    <AddProjectBranchPoliciesPanel
                        projectBranchPoliciesService={this.projectBranchPoliciesService}
                        setSelectedRefs={this.props.setSelectedRefs}
                        onDismiss={() => {
                            showAddPolicyPanel.value = false;
                        }}
                    />
                </ConditionalChildren>
            </>
        );
    }

    private zeroDataCard(hasEditPermission: boolean): JSX.Element {
        return (
            <Card key="zeroData" className="flex-center" contentProps={{ contentPadding: false }}>
                <ZeroData
                    className="settings-project-branch-policies-zero-data"
                    imagePath={this.zeroDataImagePath}
                    imageAltText={Resources.ZeroDataText}
                    secondaryText={Resources.ZeroDataText}
                />
            </Card>
        );
    }

    private addPolicyOnClick = (): void => {
        this.showAddPolicyPanel.value = true;
    };
}

Components.add("settings-project-branch-policies-view", ProjectBranchPoliciesView);
